<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

// An AI agent is a bot: its pages open under the bot's own header, with one tab per part of it,
// instead of a picker of agents above each page. Every tab is a route of its own, so a link to
// "Aurora's FAQs" stays a link.
const TABS = [
  {
    key: 'instructions',
    route: 'captain_assistants_settings_index',
    activeOn: [
      'captain_assistants_settings_index',
      'captain_assistants_settings_system_index',
      'captain_assistants_settings_audience_index',
      'captain_assistants_settings_schedule_index',
      'captain_assistants_guardrails_index',
      'captain_assistants_guidelines_index',
    ],
  },
  {
    key: 'knowledge',
    route: 'captain_assistants_knowledge_index',
    activeOn: [
      'captain_assistants_knowledge_index',
      'captain_assistants_responses_index',
      'captain_assistants_faq_suggestions',
      'captain_assistants_documents_index',
    ],
  },
  { key: 'scenarios', route: 'captain_assistants_scenarios_index' },
  { key: 'playground', route: 'captain_assistants_playground_index' },
  { key: 'inboxes', route: 'captain_assistants_inboxes_index' },
  { key: 'behavior', route: 'captain_assistants_behavior_index' },
  { key: 'performance', route: 'captain_assistants_overview_index' },
];

const KNOWLEDGE_TABS = [
  { key: 'bases', route: 'captain_assistants_knowledge_index' },
  {
    key: 'faqs',
    route: 'captain_assistants_responses_index',
    activeOn: [
      'captain_assistants_responses_index',
      'captain_assistants_faq_suggestions',
    ],
  },
  { key: 'documents', route: 'captain_assistants_documents_index' },
];

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const assistants = useMapGetter('captainAssistants/getRecords');
const agentBots = useMapGetter('agentBots/getBots');
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const toggling = ref(false);

const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() =>
  assistants.value.find(item => item.id === assistantId.value)
);
const bot = computed(() =>
  agentBots.value.find(
    item =>
      item.bot_type === 'ai_agent' &&
      Number(item.bot_config?.assistant_id) === assistantId.value
  )
);
const name = computed(() => assistant.value?.name || bot.value?.name || '');

const channelLabel = computed(() => {
  const inboxes = bot.value?.inboxes || [];
  return inboxes.length
    ? t('AGENT_BOTS.SHOW.ANSWERS_IN', {
        channels: inboxes.map(inbox => inbox.name).join(', '),
      })
    : t('AGENT_BOTS.SHOW.ANSWERS_NOWHERE');
});

const toTab = tab => ({
  name: tab.route,
  params: { accountId: route.params.accountId, assistantId: assistantId.value },
});

const isActive = tab => (tab.activeOn || [tab.route]).includes(route.name);

// A tab whose page sits behind a feature the account does not have is left out, not shown broken.
const isAvailable = tab => {
  const { featureFlag } = router.resolve(toTab(tab)).meta || {};
  return (
    !featureFlag ||
    isFeatureEnabledonAccount.value(currentAccountId.value, featureFlag)
  );
};

const tabs = computed(() =>
  TABS.filter(isAvailable).map(tab => ({
    ...tab,
    label: t(`CAPTAIN.AGENT.TABS.${tab.key.toUpperCase()}`),
  }))
);

const knowledgeTabs = computed(() =>
  KNOWLEDGE_TABS.map(tab => ({
    ...tab,
    label: t(`CAPTAIN.AGENT.KNOWLEDGE_TABS.${tab.key.toUpperCase()}`),
  }))
);

const isKnowledge = computed(() =>
  isActive(TABS.find(tab => tab.key === 'knowledge'))
);

const botsRoute = computed(() => ({
  name: 'agent_bots',
  params: { accountId: route.params.accountId },
}));

const toggleActive = async () => {
  toggling.value = true;
  try {
    await store.dispatch('agentBots/update', {
      id: bot.value.id,
      data: { active: !bot.value.active },
    });
    useAlert(
      bot.value.active
        ? t('AGENT_BOTS.TOGGLE.ENABLED')
        : t('AGENT_BOTS.TOGGLE.DISABLED')
    );
  } catch {
    useAlert(t('AGENT_BOTS.TOGGLE.ERROR'));
  } finally {
    toggling.value = false;
  }
};

onMounted(() => {
  if (!assistants.value.length) store.dispatch('captainAssistants/get');
  if (!agentBots.value.length) store.dispatch('agentBots/get');
});
</script>

<template>
  <div class="flex flex-col gap-4 pt-6">
    <router-link
      :to="botsRoute"
      class="flex items-center gap-1 text-sm w-fit text-n-slate-11 hover:text-n-slate-12"
    >
      <span class="i-lucide-chevron-left size-4" />
      {{ $t('AGENT_BOTS.HEADER') }}
    </router-link>

    <div class="flex items-center gap-4">
      <Avatar :name="name" :src="bot?.thumbnail" :size="40" class="shrink-0" />
      <div class="flex flex-col flex-grow min-w-0 gap-1">
        <div class="flex items-center gap-2 min-w-0">
          <h1 class="text-xl font-medium truncate text-n-slate-12">
            {{ name }}
          </h1>
          <span
            class="px-2 py-0.5 text-xs rounded-md shrink-0 text-n-slate-12 bg-n-alpha-2"
          >
            {{ $t('AGENT_BOTS.TYPES.AI_AGENT') }}
          </span>
        </div>
        <span class="text-sm truncate text-n-slate-11">
          {{ channelLabel }}
        </span>
      </div>
      <div v-if="bot" class="flex items-center gap-2 shrink-0">
        <span class="text-sm text-n-slate-11">
          {{
            bot.active
              ? $t('AGENT_BOTS.STATUS.ACTIVE')
              : $t('AGENT_BOTS.STATUS.INACTIVE')
          }}
        </span>
        <Switch
          :model-value="bot.active"
          :disabled="!isAdmin || toggling"
          @change="toggleActive"
        />
      </div>
    </div>

    <nav
      class="flex items-center gap-6 overflow-x-auto border-b border-n-weak no-scrollbar"
    >
      <router-link
        v-for="tab in tabs"
        :key="tab.key"
        :to="toTab(tab)"
        class="pb-2 -mb-px text-sm border-b-2 whitespace-nowrap"
        :class="
          isActive(tab)
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
        "
      >
        {{ tab.label }}
      </router-link>
    </nav>

    <nav v-if="isKnowledge" class="flex items-center gap-2">
      <router-link
        v-for="tab in knowledgeTabs"
        :key="tab.key"
        :to="toTab(tab)"
        class="px-3 py-1 text-sm rounded-lg"
        :class="
          isActive(tab)
            ? 'bg-n-alpha-2 text-n-slate-12'
            : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1'
        "
      >
        {{ tab.label }}
      </router-link>
    </nav>
  </div>
</template>
